import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import axios from 'axios';
import BookingTermsModal from './BookingTermsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DoctorBookingFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUnifiedAuth();

  // Get doctor ID from URL params or location state
  const searchParams = new URLSearchParams(location.search);
  const doctorId = searchParams.get('doctorId') || location.state?.doctorId;

  // State management
  const [doctor, setDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Patient info
  const [patientName, setPatientName] = useState(user?.name || '');
  const [patientPhone, setPatientPhone] = useState(user?.phone || '');
  const [patientEmail, setPatientEmail] = useState(user?.email || '');
  const [symptoms, setSymptoms] = useState('');

  // Terms modal
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [timingWarning, setTimingWarning] = useState(null);

  // Fetch doctor data
  useEffect(() => {
    const fetchDoctor = async () => {
      if (!doctorId) {
        setError('No doctor ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/doctors/${doctorId}`);
        console.log('✅ Doctor loaded:', response.data);
        setDoctor(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching doctor:', err);
        setError('Failed to load doctor information');
        setLoading(false);
      }
    };

    fetchDoctor();
  }, [doctorId]);

  // Generate availability when doctor is loaded
  useEffect(() => {
    if (!doctor) return;

    console.log('🔄 Generating availability for Dr.', doctor.name);
    generateAvailability();
  }, [doctor]);

  const generateAvailability = () => {
    if (!doctor || !doctor.availability) return;

    const availability = {};
    const today = new Date();
    
    // Generate 30 days of availability
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      const doctorSlots = doctor.availability[dayName];
      
      if (doctorSlots && Array.isArray(doctorSlots) && doctorSlots.length > 0) {
        availability[dateStr] = doctorSlots;
      } else {
        availability[dateStr] = [];
      }
    }

    console.log('✅ Availability generated for', Object.keys(availability).length, 'days');
    setAvailableSlots(availability);
  };

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    setSelectedTime(null);
    setCurrentStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setCurrentStep(3);
  };

  const handleCheckTermsAndBook = async () => {
    // Check booking timing
    try {
      const response = await axios.post(`${BACKEND_URL}/api/booking-policies/check-timing`, {
        appointment_date: selectedDate,
        appointment_time: selectedTime
      });
      
      setTimingWarning(response.data);
      setShowTermsModal(true);
    } catch (error) {
      console.error('Error checking timing:', error);
      setShowTermsModal(true);
    }
  };

  const handleAcceptTerms = async () => {
    setShowTermsModal(false);
    await handleConfirmBooking();
  };

  const handleConfirmBooking = async () => {
    try {
      const bookingData = {
        doctor_id: doctor.id,
        provider_type: 'doctor',
        appointment_date: selectedDate,
        appointment_time: selectedTime,
        duration: 30,
        consultation_fee: doctor.consultation_fee || 0,
        patient_name: patientName,
        patient_phone: patientPhone,
        patient_email: patientEmail,
        symptoms: symptoms,
        payment_method: 'myfatoorah',
        terms_accepted: true
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/appointments`,
        bookingData,
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );

      console.log('✅ Booking response:', response.data);
      
      const appointmentData = response.data;
      
      // Check if payment is required
      if (appointmentData.requires_payment && appointmentData.payment_url) {
        console.log('🔄 Redirecting to payment:', appointmentData.payment_url);
        // Redirect to MyFatoorah payment page
        window.location.href = appointmentData.payment_url;
        return;
      }
      
      // Navigate to confirmation (for backward compatibility)
      navigate('/appointments', {
        state: { 
          message: 'Appointment booked successfully!',
          appointment: response.data
        }
      });
    } catch (error) {
      console.error('Error booking appointment:', error);
      setError(error.response?.data?.detail || 'Failed to book appointment');
    }
  };

  const generateCalendarDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      const daySlots = availableSlots[dateStr] || [];
      const hasSlots = daySlots.length > 0;
      
      dates.push({
        date: dateStr,
        display: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        hasSlots: hasSlots,
        slotCount: hasSlots ? daySlots.length : 0
      });
    }
    
    return dates;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading doctor information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{error}</p>
          <button
            onClick={() => navigate('/doctors')}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Doctors
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Dr. {doctor.name}</h1>
          <p className="text-gray-600">{doctor.speciality}</p>
          <p className="text-sm text-gray-500 mt-2">{doctor.clinic_address}</p>
          {doctor.consultation_fee > 0 && (
            <p className="text-lg font-semibold text-blue-600 mt-2">
              Consultation Fee: {doctor.consultation_fee} KWD
            </p>
          )}
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between">
            {['Choose Date', 'Pick Time', 'Confirm Details'].map((step, idx) => (
              <div key={idx} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center font-semibold
                  ${currentStep > idx + 1 ? 'bg-green-500 text-white' : 
                    currentStep === idx + 1 ? 'bg-blue-600 text-white' : 
                    'bg-gray-200 text-gray-500'}
                `}>
                  {currentStep > idx + 1 ? '✓' : idx + 1}
                </div>
                <span className={`ml-2 text-sm ${currentStep === idx + 1 ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                  {step}
                </span>
                {idx < 2 && <div className="w-16 h-0.5 bg-gray-300 mx-2"></div>}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Date Selection */}
        {currentStep === 1 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Choose a Date</h2>
            
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {generateCalendarDates().map(dateObj => (
                <button
                  key={dateObj.date}
                  onClick={() => dateObj.hasSlots && handleDateSelect(dateObj.date)}
                  disabled={!dateObj.hasSlots}
                  className={`
                    p-3 rounded-lg border-2 text-center transition-all
                    ${!dateObj.hasSlots ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' :
                      selectedDate === dateObj.date ? 'bg-blue-600 border-blue-600 text-white' :
                      'border-gray-300 hover:border-blue-500 hover:bg-blue-50'}
                  `}
                >
                  <div className="text-xs font-medium">{dateObj.dayName}</div>
                  <div className="text-lg font-bold">{dateObj.display.split(' ')[1]}</div>
                  <div className="text-xs">{dateObj.display.split(' ')[0]}</div>
                  {dateObj.hasSlots && (
                    <div className="text-xs mt-1 text-green-600 font-medium">
                      {dateObj.slotCount} slots
                    </div>
                  )}
                  {!dateObj.hasSlots && (
                    <div className="text-xs mt-1">No slots</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Time Selection */}
        {currentStep === 2 && selectedDate && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Select Time</h2>
            <p className="text-gray-700 mb-4">Date: <span className="font-semibold">{selectedDate}</span></p>
            
            {availableSlots[selectedDate] && availableSlots[selectedDate].length > 0 ? (
              <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                {availableSlots[selectedDate].map(time => (
                  <button
                    key={time}
                    onClick={() => handleTimeSelect(time)}
                    className={`
                      p-3 rounded-lg border text-sm font-medium transition-all
                      ${selectedTime === time ?
                        'bg-blue-600 border-blue-600 text-white' :
                        'border-gray-300 hover:border-blue-500 hover:bg-blue-50'}
                    `}
                  >
                    {time}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No available time slots for this date</p>
            )}
            
            <button
              onClick={() => setCurrentStep(1)}
              className="mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Back to Date Selection
            </button>
          </div>
        )}

        {/* Step 3: Confirm Details */}
        {currentStep === 3 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Confirm Your Appointment</h2>
            
            {/* Booking Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold mb-2">Appointment Summary</h3>
              <div className="space-y-1 text-sm">
                <p><strong>Doctor:</strong> Dr. {doctor.name}</p>
                <p><strong>Speciality:</strong> {doctor.speciality}</p>
                <p><strong>Date:</strong> {selectedDate}</p>
                <p><strong>Time:</strong> {selectedTime}</p>
                <p><strong>Duration:</strong> 30 minutes</p>
                {doctor.consultation_fee > 0 && (
                  <p className="text-lg font-bold text-blue-600 mt-2">
                    Consultation Fee: {doctor.consultation_fee} KWD
                  </p>
                )}
              </div>
            </div>

            {/* User Information */}
            <div className="space-y-4">
              <h3 className="font-semibold">Your Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  value={patientPhone}
                  onChange={(e) => setPatientPhone(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={patientEmail}
                  onChange={(e) => setPatientEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Symptoms/Reason for Visit</label>
                <textarea
                  value={symptoms}
                  onChange={(e) => setSymptoms(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Brief description of your symptoms or reason for consultation"
                />
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setCurrentStep(2)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Back
              </button>
              <button
                onClick={handleCheckTermsAndBook}
                disabled={!patientName || !patientPhone || !patientEmail}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Review Terms & Confirm Appointment
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Terms Modal */}
      <BookingTermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleAcceptTerms}
        bookingDetails={{
          appointment_date: selectedDate,
          appointment_time: selectedTime
        }}
        timingWarning={timingWarning}
      />
    </div>
  );
};

export default DoctorBookingFlow;
